import { useParams } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { Seo } from "@/lib/seo";
import { Loading } from "@/components/States";
import { DetailError } from "@/components/DetailError";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { VenueCard, EventCard, StayAreaCard } from "@/components/Cards";
import { CTAButton } from "@/components/CTAButton";
import { onImgError } from "@/lib/images";
import { Plane } from "lucide-react";

export default function CityPage() {
  const { slug } = useParams();
  const { data, loading, error } = useFetch(`/city-guide/${slug}`, [slug]);

  if (loading) return <Loading />;
  if (error || !data?.city) return <DetailError error={error} path={`/cities/${slug}`} notFound={!error} />;
  const c = data.city;

  return (
    <>
      <Seo title={c.seo_title || `${c.name} Travel Guide | Hotelstrips`} description={c.seo_description || c.description}
        image={c.image_url} path={`/cities/${c.slug}`} type="article" />

      <section className="relative min-h-[52vh] flex items-end">
        <img src={c.image_url} alt={c.image_alt || c.name} onError={onImgError} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 img-overlay" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full pb-12">
          <div className="overline text-primary mb-2">{c.state}, {c.country} • City guide</div>
          <h1 className="font-heading text-4xl sm:text-6xl font-black tracking-tighter text-white">{c.name}</h1>
          {c.airports?.length > 0 && (
            <div className="flex items-center gap-2 mt-3 text-white/80 text-sm"><Plane className="h-4 w-4" /> Airports: {c.airports.join(", ")}</div>
          )}
          <div className="mt-6" data-testid="city-hero-cta">
            <CTAButton
              productType="hotels"
              size="lg"
              label={`Find hotels in ${c.name}`}
              testId="city-hero-hotels-cta"
              ctx={{ city: c.slug, page: `city_${c.slug}`, position: "hero" }}
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
        <Breadcrumbs items={[{ label: "Cities" }, { label: c.name }]} />
        <p className="text-lg text-foreground/80 leading-relaxed max-w-3xl mb-12">{c.description}</p>

        {data.venues?.length > 0 && (
          <div className="mb-14">
            <h2 className="font-heading text-3xl font-extrabold tracking-tighter text-ink mb-5">Venues in {c.name}</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{data.venues.map((v) => <VenueCard key={v.slug} venue={v} />)}</div>
          </div>
        )}

        {data.stay_areas?.length > 0 && (
          <div className="mb-14">
            <h2 className="font-heading text-3xl font-extrabold tracking-tighter text-ink mb-5">Stay areas</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.stay_areas.map((a, i) => <StayAreaCard key={a.slug} area={a} index={i} ctx={{ city: c.slug, page: `city_${c.slug}` }} />)}
            </div>
          </div>
        )}

        {data.events?.length > 0 && (
          <div className="mb-14">
            <h2 className="font-heading text-3xl font-extrabold tracking-tighter text-ink mb-5">Upcoming events</h2>
            <div className="grid gap-4 md:grid-cols-2">{data.events.map((e) => <EventCard key={e.slug} event={e} />)}</div>
          </div>
        )}

        <div className="rounded-2xl bg-ink text-white p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <h3 className="font-heading text-2xl font-bold tracking-tight">Book your {c.name} trip</h3>
          <div className="flex flex-wrap gap-3">
            <CTAButton productType="hotels" ctx={{ city: c.slug, page: `city_${c.slug}`, position: "city_band" }} />
            <CTAButton productType="flights" variant="outline" className="!text-white !border-white/30" ctx={{ city: c.slug, page: `city_${c.slug}`, position: "city_band" }} />
          </div>
        </div>
      </div>
    </>
  );
}
